// AGC: MachinePhotoUpload - Upload machine photo from camera or gallery
// AGC: Supports camera capture and gallery selection

import React, { useState, useRef } from 'react'
import { Camera, Image as ImageIcon, X, Upload } from 'lucide-react'
import toast from 'react-hot-toast'

interface MachinePhotoUploadProps {
  currentPhoto?: string
  onPhotoChange: (photo: File | null) => void
}

/**
 * AGC: MachinePhotoUpload - Photo upload for machines
 * AGC: Features:
 * AGC: - Camera capture
 * AGC: - Gallery selection
 * AGC: - Photo preview
 * AGC: - Remove photo
 */
export const MachinePhotoUpload: React.FC<MachinePhotoUploadProps> = ({
  currentPhoto,
  onPhotoChange,
}) => {
  const [preview, setPreview] = useState<string | null>(currentPhoto || null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem')
      return
    }

    // AGC: Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreview(reader.result as string)
    }
    reader.readAsDataURL(file)

    // AGC: Pass file to parent
    onPhotoChange(file)
  }

  const handleGalleryClick = () => {
    fileInputRef.current?.click()
  }

  const handleCameraClick = () => {
    cameraInputRef.current?.click()
  }

  const handleRemovePhoto = () => {
    setPreview(null)
    onPhotoChange(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (cameraInputRef.current) cameraInputRef.current.value = ''
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Foto da Máquina
      </label>

      {/* AGC: Preview */}
      {preview ? (
        <div className="relative">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-cover rounded-xl border-2 border-gray-300 dark:border-gray-700"
          />
          <button
            type="button"
            onClick={handleRemovePhoto}
            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-6 text-center bg-gray-50 dark:bg-gray-800/50">
          <div className="flex flex-col items-center gap-3">
            <Upload className="w-12 h-12 text-gray-400" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Adicione uma foto da máquina
            </p>
          </div>
        </div>
      )}

      {/* AGC: Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleCameraClick}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-navy-700 text-white rounded-xl hover:bg-navy-800 transition-colors font-medium"
        >
          <Camera className="w-5 h-5" />
          Câmera
        </button>
        <button
          type="button"
          onClick={handleGalleryClick}
          className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-800 transition-colors font-medium"
        >
          <ImageIcon className="w-5 h-5" />
          Galeria
        </button>
      </div>

      {/* AGC: Hidden file inputs */}
      {/* AGC: Camera input (capture from camera) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleFileSelect(file)
        }}
        className="hidden"
      />

      {/* AGC: Gallery input (select from gallery) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleFileSelect(file)
        }}
        className="hidden"
      />
    </div>
  )
}

export default MachinePhotoUpload
